import { cn } from '../../../../lib/utils';

export interface ProcessingIndicatorLoadingSpinnerProps {
  /** 로딩 진행률 값 (0-100) */
  value?: number;
  /** 스피너의 크기 (픽셀 단위) */
  size?: number;
  /** 스피너 테두리의 두께 */
  strokeWidth?: number;
  /** 스피너 배경색 */
  bgColor?: string;
  /** 진행 상태를 나타내는 색상 */
  progressColor?: string;
  /** 스피너의 회전 방향 - 시계 방향 또는 반시계 방향 */
  direction?: 'clockwise' | 'counterclockwise';
  /** 진행률 퍼센티지를 표시할지 여부 */
  showPercentage?: boolean;
  /** 퍼센티지 텍스트의 폰트 크기 */
  percentageFontSize?: number;
  /** 퍼센티지 요소에 적용할 CSS 클래스 */
  percentageClassName?: string;
}

export default function ProcessingIndicatorLoadingSpinner({
  value = 0,
  size = 24,
  strokeWidth = 3,
  bgColor = 'text-sys-style-neutral-default',
  progressColor = 'text-sys-cont-primary-default',
  direction = 'counterclockwise',
  showPercentage = true,
  percentageFontSize = 6,
  percentageClassName,
}: ProcessingIndicatorLoadingSpinnerProps) {
  // 값을 0-100 범위로 제한
  const normalizedValue = Math.min(100, Math.max(0, value));

  // SVG 원의 속성 계산
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // 진행도에 따른 strokeDashoffset 계산
  // 시계 방향은 기존과 같이 계산하고, 반시계 방향은 값이 0일 때 -circumference, 100일 때 0이 되도록 계산.
  const strokeDashoffset =
    direction === 'clockwise'
      ? circumference - (normalizedValue / 100) * circumference
      : (normalizedValue / 100) * circumference - circumference;

  return (
    <div className="relative flex items-center justify-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="rotate-[-90deg]"
      >
        {/* 배경 원 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className={cn('stroke-current transition-all', bgColor)}
        />

        {/* 진행 원 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={cn('stroke-current transition-all', progressColor)}
        />
      </svg>

      {/* 가운데 퍼센트 표시 */}
      {showPercentage && (
        <div
          className={cn(
            'absolute text-comp-accent-xsmall',
            percentageClassName,
          )}
        >
          <p
            style={{
              ...(percentageFontSize && {
                fontSize: `${percentageFontSize}px`,
              }),
            }}
          >
            {normalizedValue}%
          </p>
        </div>
      )}
    </div>
  );
}
